import React, { useState, useEffect } from "react";
import axios from "axios";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import DynamicForm from '../common/DynamicForm';
import LoaderComponent from "../../../libraries/loader/loader";
import { Fetchexecutequery, Fetchheaderdata, Fetchupdatequery } from '../../../services/process/BRS1/brsService7';
import { CreateData } from '../../../services/process/process_data/process_data';
import { Icon } from 'semantic-ui-react';

// ─────────────────────────────────────────────
// TAB CATEGORY CONFIGURATION
// Replace headerConfigId values as needed.
// ─────────────────────────────────────────────
const TAB_CATEGORIES = [
  {
    key: "core",
    label: "Core Masters",
    icon: "database",
    tabs: [
      { key: "student",     label: "Students",     table: "students",     headerConfigId: 99  },
      { key: "institution", label: "Institutions",  table: "institutions", headerConfigId: 100 },
      { key: "influencer",  label: "Influencers",   table: "influencers",  headerConfigId: 101 },
      { key: "program",     label: "Programs",      table: "programs",     headerConfigId: 107 },
      { key: "application", label: "Applications",  table: "applications", headerConfigId: 103 },
    ],
  },
  {
    key: "events",
    label: "Events",
    icon: "calendar",
    tabs: [
      { key: "event",            label: "Events",                table: "events",                       headerConfigId: 108 },
      { key: "event_reg",        label: "Event Registrations",   table: "event_student_registrations",  headerConfigId: 105 },
      { key: "event_institution",label: "Event Institutions",    table: "event_institutions",           headerConfigId: 106 },
    ],
  },
  {
    key: "supporting_data",
    label: "Supporting Data",
    icon: "settings",
    tabs: [
      { key: "country",          label: "Countries",            table: "countries",            headerConfigId: 107 },
      { key: "state",            label: "States",               table: "states",               headerConfigId: 108 },
      { key: "city",             label: "Cities",               table: "cities",               headerConfigId: 109 },
      { key: "currency",         label: "Currencies",           table: "currencies",           headerConfigId: 110 },
      { key: "language",         label: "Languages",            table: "languages",            headerConfigId: 111 },
      { key: "degree_level",     label: "Degree Levels",        table: "degree_levels",        headerConfigId: 112 },
      { key: "education_level",  label: "Education Levels",     table: "education_levels",     headerConfigId: 113 },
      { key: "discipline",       label: "Disciplines",          table: "disciplines",          headerConfigId: 114 },
      { key: "intake_term",      label: "Intake Terms",         table: "intake_terms",         headerConfigId: 115 },
    ],
  },
  {
    key: "config",
    label: "Configuration",
    icon: "sliders",
    tabs: [
      { key: "tenant_type",         label: "Tenant Types",          table: "tenant_types",          headerConfigId: 116 },
      { key: "institution_type",    label: "Institution Types",     table: "institution_types",     headerConfigId: 117 },
      { key: "event_type",          label: "Event Types",           table: "event_types",           headerConfigId: 118 },
      { key: "application_status",  label: "Application Statuses",  table: "application_statuses",  headerConfigId: 119 },
      { key: "visa_status",         label: "Visa Statuses",         table: "visa_statuses",         headerConfigId: 120 },
      { key: "passport_status",     label: "Passport Statuses",     table: "passport_statuses",     headerConfigId: 121 },
    ],
  },
];

// Flat list of all tabs for lookup
const ALL_TABS = TAB_CATEGORIES.flatMap((cat) => cat.tabs);

// Build initial per-tab state maps
const buildInitialTabState = (defaultValue) =>
  Object.fromEntries(ALL_TABS.map((t) => [t.key, defaultValue]));

// ─────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────
const styles = {
  wrapper: {
    fontFamily: "'Segoe UI', sans-serif",
    padding: "12px 16px",
    backgroundColor: "#f5f7fb",
    minHeight: "100vh",
  },
  pageTitle: {
    color: "#4969ab",
    fontSize: 18,
    fontWeight: 600,
    textAlign: "center",
    margin: "0 0 16px",
    letterSpacing: "0.03em",
    textTransform: "uppercase",
  },
  errorBanner: {
    backgroundColor: "#fff0f0",
    color: "#c0392b",
    border: "1px solid #f5c6c6",
    borderRadius: 6,
    padding: "8px 12px",
    marginBottom: 12,
    fontSize: 13,
  },
  // ── Category tab bar ──
  categoryBar: {
    display: "flex",
    gap: 6,
    marginBottom: 0,
    borderBottom: "2px solid #d0d7e8",
  },
  categoryTab: (active) => ({
    padding: "8px 18px",
    border: "1px solid",
    borderColor: active ? "#4969ab" : "#d0d7e8",
    borderBottom: active ? "2px solid #fff" : "1px solid #d0d7e8",
    borderRadius: "6px 6px 0 0",
    marginBottom: active ? -2 : 0,
    background: active ? "#fff" : "#edf1f9",
    color: active ? "#4969ab" : "#5a6a88",
    fontWeight: active ? 600 : 400,
    fontSize: 13,
    cursor: "pointer",
    userSelect: "none",
    display: "flex",
    alignItems: "center",
    gap: 6,
    transition: "all 0.15s",
  }),
  // ── Category panel ──
  categoryPanel: {
    background: "#fff",
    border: "1px solid #d0d7e8",
    borderTop: "none",
    borderRadius: "0 6px 6px 6px",
    padding: "12px 14px",
  },
  // ── Sub-tab bar ──
  subTabBar: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
    marginBottom: 14,
    borderBottom: "1.5px solid #e3e8f0",
    paddingBottom: 0,
  },
  subTab: (active) => ({
    padding: "6px 14px",
    border: "1px solid",
    borderColor: active ? "#4969ab" : "transparent",
    borderBottom: active ? "2px solid #fff" : "1.5px solid transparent",
    marginBottom: active ? -1.5 : 0,
    borderRadius: "4px 4px 0 0",
    background: active ? "#eef2fb" : "transparent",
    color: active ? "#4969ab" : "#7a8aaa",
    fontWeight: active ? 600 : 400,
    fontSize: 12,
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.12s",
  }),
  // ── Form + grid area ──
  formArea: {
    marginBottom: 10,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "8px 0",
  },
  searchInput: {
    width: "28%",
    padding: "7px 10px",
    borderRadius: 4,
    border: "1px solid #ced4da",
    fontSize: 13,
    backgroundColor: "#fff",
  },
  submitBtn: (isEdit) => ({
    padding: "7px 22px",
    borderRadius: 4,
    border: "none",
    background: isEdit ? "#e67e22" : "#4969ab",
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  }),
  clearBtn: {
    padding: "7px 14px",
    borderRadius: 4,
    border: "1px solid #ced4da",
    background: "#fff",
    color: "#5a6a88",
    fontSize: 13,
    cursor: "pointer",
    marginRight: 8,
  },
  gridWrapper: {
    display: "flex",
    justifyContent: "center",
  },
  editBadge: {
    display: "inline-block",
    background: "#fff3e0",
    color: "#e67e22",
    border: "1px solid #f0c080",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    marginLeft: 8,
    verticalAlign: "middle",
  },
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
function MasterMaintenance() {
  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials"))
  );

  // Active category key
  const [activeCategoryKey, setActiveCategoryKey] = useState(TAB_CATEGORIES[0].key);
  // Active sub-tab per category
  const [activeSubTabByCategory, setActiveSubTabByCategory] = useState(
    Object.fromEntries(TAB_CATEGORIES.map((cat) => [cat.key, cat.tabs[0].key]))
  );

  // Per-tab data state
  const [formDataByTab,   setFormDataByTab]   = useState(buildInitialTabState({}));
  const [fieldsByTab,     setFieldsByTab]     = useState(buildInitialTabState(null));
  const [rowDataByTab,    setRowDataByTab]    = useState(buildInitialTabState([]));
  const [columnDefsByTab, setColumnDefsByTab] = useState(buildInitialTabState([]));
  const [loadingByTab,    setLoadingByTab]    = useState(buildInitialTabState(false));
  const [searchTextByTab, setSearchTextByTab] = useState(buildInitialTabState(""));
  const [editModeByTab,   setEditModeByTab]   = useState(buildInitialTabState(false));
  const [fetchedTabs,     setFetchedTabs]     = useState(new Set());

  const [error, setError] = useState(null);

  // Derived: active sub-tab key
  const activeSubTabKey = activeSubTabByCategory[activeCategoryKey];
  const activeTabConfig = ALL_TABS.find((t) => t.key === activeSubTabKey);
  const isEditMode      = editModeByTab[activeSubTabKey] || false;

  // ── Lazy-load: fetch when a sub-tab is first activated ──
  useEffect(() => {
    if (activeTabConfig && !fetchedTabs.has(activeSubTabKey)) {
      fetchTabInfo(activeTabConfig);
      setFetchedTabs((prev) => new Set(prev).add(activeSubTabKey));
    }
  }, [activeSubTabKey, activeCategoryKey]);

  const formatColumnName = (name) =>
    name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const fetchTabInfo = async (tab) => {
    setLoadingByTab((prev) => ({ ...prev, [tab.key]: true }));
    setError(null);
    try {
      const headerPayload = { headerconfig_id: tab.headerConfigId, filterconditions: [] };
      const headerCreds   = Fetchheaderdata();
      const headerResponse = await axios.post(headerCreds[1], headerPayload, headerCreds[0]);

      const fields  = (headerResponse.data || {}).fields || [];
      const columns = fields.map((field) => ({
        headerName: formatColumnName(field.columnName),
        field:      field.columnName,
        filter:     true,
        resizable:  true,
      }));
      columns.push({
        headerName:             "Edit",
        field:                  "edit",
        width:                  80,
        cellRendererFramework:  (params) => (
          <Icon
            name="edit"
            style={{ cursor: "pointer", color: "#4969ab" }}
            onClick={() => handleEditClick(tab.key, params.data)}
          />
        ),
      });

      setFieldsByTab((prev)     => ({ ...prev, [tab.key]: fields }));
      setColumnDefsByTab((prev) => ({ ...prev, [tab.key]: columns }));

      const dataCreds   = Fetchexecutequery();
      const dataResponse = await axios.post(
        dataCreds[1],
        { query: `SELECT * FROM education.${tab.table};` },
        dataCreds[0]
      );
      setRowDataByTab((prev) => ({ ...prev, [tab.key]: dataResponse.data || [] }));
    } catch (err) {
      console.error(`Error fetching ${tab.table}:`, err);
      setError(`Failed to load data for "${tab.label}". Please try again.`);
    } finally {
      setLoadingByTab((prev) => ({ ...prev, [tab.key]: false }));
    }
  };

  const handleEditClick = (tabKey, row) => {
    setFormDataByTab((prev)  => ({ ...prev, [tabKey]: { ...row } }));
    setEditModeByTab((prev)  => ({ ...prev, [tabKey]: true }));
  };

  const handleClear = () => {
    setFormDataByTab((prev)  => ({ ...prev, [activeSubTabKey]: {} }));
    setEditModeByTab((prev)  => ({ ...prev, [activeSubTabKey]: false }));
  };

  const handleCreate = () => {
    const payload = {
      database: "education",
      table:    activeTabConfig.table,
      formData: { ...formDataByTab[activeSubTabKey] },
    };
    const creds = CreateData();
    axios.post(creds[1], payload, creds[0])
      .then(() => {
        fetchTabInfo(activeTabConfig);
        handleClear();
      })
      .catch((err) => {
        console.error("Create error:", err);
        setError("Error creating record.");
      });
  };

  const handleUpdate = () => {
    const tabFormData    = { ...formDataByTab[activeSubTabKey] };
    const recordId       = tabFormData.id || tabFormData.primary_id;
    const setData        = { ...tabFormData };
    delete setData.primary_id;
    delete setData.id;

    const payload = {
      schema_name: "education",
      table:       activeTabConfig.table,
      set_column:  setData,
      where:       [{ id: recordId }],
    };
    const creds = Fetchupdatequery();
    axios.post(creds[1], payload, creds[0])
      .then(() => {
        fetchTabInfo(activeTabConfig);
        handleClear();
      })
      .catch((err) => {
        console.error("Update error:", err);
        setError("Error updating record.");
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    isEditMode ? handleUpdate() : handleCreate();
  };

  const handleInputChange = (name, value) => {
    setFormDataByTab((prev) => ({
      ...prev,
      [activeSubTabKey]: { ...(prev[activeSubTabKey] || {}), [name]: value },
    }));
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  const activeCategory = TAB_CATEGORIES.find((c) => c.key === activeCategoryKey);

  return (
    <div style={styles.wrapper}>
      <h2 style={styles.pageTitle}>
        Master Maintenance
      </h2>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* ── Category Tabs ── */}
      <div style={styles.categoryBar}>
        {TAB_CATEGORIES.map((cat) => (
          <div
            key={cat.key}
            style={styles.categoryTab(cat.key === activeCategoryKey)}
            onClick={() => setActiveCategoryKey(cat.key)}
          >
            <Icon name={cat.icon} style={{ fontSize: 14, margin: 0 }} />
            {cat.label}
          </div>
        ))}
      </div>

      {/* ── Category Panel ── */}
      <div style={styles.categoryPanel}>
        {/* Sub-tab bar */}
        <div style={styles.subTabBar}>
          {activeCategory.tabs.map((tab) => (
            <div
              key={tab.key}
              style={styles.subTab(tab.key === activeSubTabKey)}
              onClick={() =>
                setActiveSubTabByCategory((prev) => ({
                  ...prev,
                  [activeCategoryKey]: tab.key,
                }))
              }
            >
              {tab.label}
              {editModeByTab[tab.key] && (
                <span style={{ ...styles.editBadge, fontSize: 9, padding: "1px 5px", marginLeft: 5 }}>
                  EDITING
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Content for active sub-tab */}
        {activeTabConfig && (
          loadingByTab[activeSubTabKey] ? (
            <LoaderComponent loaderOpen={true} />
          ) : (
            <>
              {/* Edit mode indicator */}
              {isEditMode && (
                <div style={{
                  background: "#fff8f0",
                  border: "1px solid #f0c080",
                  borderRadius: 4,
                  padding: "5px 12px",
                  marginBottom: 10,
                  fontSize: 12,
                  color: "#b7730a",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}>
                  <Icon name="pencil" style={{ fontSize: 12, margin: 0 }} />
                  Editing an existing record — make your changes and click Update.
                </div>
              )}

              {/* Dynamic Form */}
              <div style={styles.formArea}>
                <DynamicForm
                  fields={fieldsByTab[activeSubTabKey] || []}
                  formData={formDataByTab[activeSubTabKey] || {}}
                  onInputChange={handleInputChange}
                />
              </div>

              {/* Toolbar: search + buttons */}
              <div style={styles.toolbar}>
                <input
                  type="text"
                  placeholder={`Search ${activeTabConfig.label}…`}
                  value={searchTextByTab[activeSubTabKey] || ""}
                  onChange={(e) =>
                    setSearchTextByTab((prev) => ({
                      ...prev,
                      [activeSubTabKey]: e.target.value,
                    }))
                  }
                  style={styles.searchInput}
                />

                <div style={{ display: "flex", alignItems: "center" }}>
                  {isEditMode && (
                    <button style={styles.clearBtn} onClick={handleClear}>
                      Cancel
                    </button>
                  )}
                  <button
                    style={styles.submitBtn(isEditMode)}
                    onClick={handleSubmit}
                  >
                    <Icon
                      name={isEditMode ? "save" : "plus"}
                      style={{ fontSize: 13, margin: 0 }}
                    />
                    {isEditMode ? "Update" : "Create"}
                  </button>
                </div>
              </div>

              {/* AG Grid */}
              <div style={styles.gridWrapper}>
                {columnDefsByTab[activeSubTabKey]?.length ? (
                  <div className="ag-theme-alpine" style={{ height: 400, width: "100%" }}>
                    <AgGridReact
                      columnDefs={columnDefsByTab[activeSubTabKey]}
                      rowData={rowDataByTab[activeSubTabKey] || []}
                      quickFilterText={searchTextByTab[activeSubTabKey] || ""}
                      pagination
                      paginationPageSize={9}
                    />
                  </div>
                ) : (
                  <div style={{ color: "#aaa", padding: "20px 0", fontSize: 13 }}>
                    No data available for this table yet.
                  </div>
                )}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

export default MasterMaintenance;